import { ZipWriter, BlobWriter, BlobReader, TextReader } from '@zip.js/zip.js'
import { convertDateTimeToString } from '@/utils/dateUtil/dateUtils'
import type { UpdateData } from '../utils/interface'
import { generatePassword } from '../utils/passwordGenerator'

/**
 * zipファイルのBlobを作成する
 * @param updateData Blobにするデータ
 * @returns 作成されたblobとファイル名
 */
export const createZipBlob = async (updateData: UpdateData[]): Promise<[Blob | null, string]> => {
  const isCreateZip = { complete: false }

  const zipFileName = convertDateTimeToString(new Date())
  const password = await generatePassword(updateData[0].inspectionResult.govCode)
  const zipComplete = new ZipWriter(new BlobWriter('application/zip'), { password })

  // データをアップロードする際の照合として空ファイルを生成する
  await zipComplete.add(zipFileName)
  // データ格納
  const tasks = updateData.map(async data => {
    isCreateZip.complete = true
    return await addInspectionResultBlob(data, zipComplete)
  })
  Promise.all(tasks).catch(e => {
    throw e
  })

  // 以下のエラーは通常発生しない(渡すデータには必ずzipデータを作成できるデータが一つ以上含まれているため)
  if (!isCreateZip.complete) {
    throw new Error('データが存在しない')
  }

  return [await zipComplete.close(), zipFileName]
}

/**
 * 指定されたzipファイルに調査データを追加する
 * @param updateData indexedDBから取得したデータ
 * @param zipFile 調査データを保存するzipファイル
 * @returns なし
 */
const addInspectionResultBlob = async (
  updateData: UpdateData,
  zipFile: ZipWriter<Blob>
): Promise<void> => {
  const { inspectionResult, photoStates } = updateData
  const jsonData = {
    inspectionResult,
    photoStates
  }
  const folderName = inspectionResult.assessmentId
  const tasks = updateData.photoStates
    .filter(
      // indexedDBから取得した画像データに画像が保存されていないもしくはS3に保存されている場合はzipファイルに追加しない
      photoState => photoState.arrayBuffer !== undefined && !photoState.isOnS3
    )
    .map(async photoState => {
      const photoBlob = new Blob([photoState.arrayBuffer!])
      // arrayBufferは画像として保存するためjsonからは削除する
      delete photoState.arrayBuffer
      // 画像はphotosフォルダに保存する
      return await zipFile.add(
        `${folderName}/photos/${photoState.fileName + photoState.extension}`,
        new BlobReader(photoBlob)
      )
    })
  Promise.all([
    ...tasks,
    zipFile.add(`${folderName}/result.json`, new TextReader(JSON.stringify(jsonData)))
  ]).catch(e => {
    console.error('addInspectionResultBlob error')
    throw e
  })
}
